import {createSelector} from 'reselect';
import get from 'lodash/get';

import getListingPageModalStates from './modalStates';
import {getSystem, getTranslations, getTripData} from './store';
import {getBaseUrlPath, getIsMobile, getLocaleJS, getSiteName} from './system';
import {getTravelerSegmentFeatures} from './travelerSegmentFeatures';
import {availableRoutes} from '../routes';
import {getPreferredLocation, getSpu} from './listing';
import {getIsTypeaheadInputFocused} from './listingPageState';
import {getCookiedGeoString} from '../utils/travelerSegments';

const getLabels = createSelector(
    [getTranslations, getTravelerSegmentFeatures],
    (translations, travelerSegmentFeatures) => {
        return {
            searchMoreProperties: translations.searchMoreProperties,
            mobileTitle: translations.search,
            buttonTitle: travelerSegmentFeatures.useCheckAvailabilityButton
                ? translations.checkAvailability
                : translations.search,
            datePicker: {
                label: translations.checkIn,
                label2: translations.checkOut,
                clear: translations.clearDates,
                done: translations.done
            },
            guests: {
                label: translations.searchGuestsLabel,
                adult: translations.adultSingular,
                adults: translations.adultPlural,
                child: translations.childSingular,
                children: translations.childPlural,
                pets: translations.pets,
                guests: translations.guests,
                yesBtn: translations.guestSelectorYes,
                noBtn: translations.guestSelectorNo,
                clearBtn: translations.clear,
                applyBtn: translations.apply,
                modalTitle: translations.selectGuests,
                mobileBtn: translations.continue
            }
        };
    }
);

const getLocation = createSelector(
    [getSystem, getTravelerSegmentFeatures, getPreferredLocation],
    (system, travelerSegmentFeatures, preferredLocation) => {
        if (travelerSegmentFeatures.showLastSearchedLocation) {
            return getCookiedGeoString(system.cookies);
        }
        const preferredLocationName = get(preferredLocation, 'name.full');
        if (preferredLocationName) {
            return preferredLocationName;
        }

        return null;
    }
);

const getGuests = createSelector(
    [getTripData],
    (tripData) => {
        return {
            adultsCount: tripData.adults,
            childrenCount: tripData.children,
            petIncluded: tripData.hasPets,
            adultsMin: 1
        };
    }
);

const getDates = createSelector(
    [getTripData],
    (tripData) => {
        return {
            arrive: tripData.arriveDateText,
            depart: tripData.departDateText
        };
    }
);

const getRoutes = createSelector(
    [getBaseUrlPath],
    (baseUrlPath) => {
        return {
            search: `${baseUrlPath}?r=${availableRoutes.SEARCH}`,
            datePicker: `${baseUrlPath}?r=${availableRoutes.SEARCH_DATE_PICKER}`,
            guestPicker: `${baseUrlPath}?r=${availableRoutes.SEARCH_GUEST_PICKER}`
        };
    }
);

const getHideDates = createSelector(
    [],
    () => false
);

const getHideGuests = createSelector(
    [getIsMobile, getTravelerSegmentFeatures],
    (isMobile, travelerSegmentFeatures) =>
        !isMobile || travelerSegmentFeatures.hideSearchBoxGuestsMobile
);

const getSearchBoxSelector = createSelector(
    [
        getLabels,
        getListingPageModalStates,
        getLocation,
        getIsMobile,
        getLocaleJS,
        getSiteName,
        getTravelerSegmentFeatures,
        getGuests,
        getHideGuests,
        getDates,
        getHideDates,
        getIsTypeaheadInputFocused,
        getRoutes,
        getSpu
    ],
    (
        labels,
        modalStates,
        location,
        isMobile,
        localeJS,
        systemSiteName,
        travelerSegmentFeatures,
        guests,
        hideGuests,
        dates,
        hideDates,
        isTypeaheadInputFocused,
        routes,
        spu
    ) => {
        return {
            isMobile,
            labels,
            location,
            localeJS,
            siteName: systemSiteName.toLowerCase(),
            guests,
            hideGuests,
            checkIn: dates.arrive,
            checkOut: dates.depart,
            hideDates,
            searchRoute: routes.search,
            spu,
            datePickerRoute: routes.datePicker,
            guestPickerRoute: routes.guestPicker,
            isSearchModalOpen: modalStates.searchModalOpen,
            isSearchDatePickerOpen: modalStates.searchDatePickerOpen,
            isSearchGuestPickerOpen: modalStates.searchGuestPickerOpen,
            isTypeaheadInputFocused,
            useCheckAvailabilityButton: travelerSegmentFeatures.useCheckAvailabilityButton,
            usePrimarySearchBoxSubmit: travelerSegmentFeatures.usePrimarySearchBoxSubmit
        };
    }
);

export default getSearchBoxSelector;
export {
    getLabels,
    getLocation,
    getSearchBoxSelector,
    getGuests,
    getDates,
    getRoutes,
    getHideDates,
    getHideGuests
};
